<template>
  <div class="pricing-page">
    <div class="pricing-container">
      <section class="pricing-hero pricing-panel">
        <div class="pricing-hero-copy">
          <p class="eyebrow">JOIN THE WINNER'S CLUB</p>
          <h1>Bet like a Pro.</h1>
          <p>
            One subscription for the iOS app and the Pro Dashboard: alerts, quick stats,
            conversational data access, and market coverage built around real money movement.
          </p>
        </div>
        <div class="price-card">
          <span class="price-label">Monthly</span>
          <div class="price-row">
            <span class="price">$24.99</span>
            <span class="term">/ month</span>
          </div>
          <p>Includes mobile app access and the full Pro Dashboard experience.</p>
          <a :href="IOS_APP_STORE_URL" target="_blank" rel="noopener noreferrer" class="btn btn-primary">
            Download iOS App
          </a>
        </div>
      </section>

      <section class="section-card pricing-panel accent-blue">
        <div class="section-head">
          <span class="section-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M7 2.75h10A2.25 2.25 0 0 1 19.25 5v14A2.25 2.25 0 0 1 17 21.25H7A2.25 2.25 0 0 1 4.75 19V5A2.25 2.25 0 0 1 7 2.75Z" stroke="currentColor" stroke-width="1.5" />
              <path d="M10 18h4M9 6.5h6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </span>
          <h2>What You Get</h2>
        </div>

        <div class="pricing-table-wrap">
          <table class="pricing-table">
            <thead>
              <tr>
                <th scope="col">Included product</th>
                <th scope="col">Core access</th>
                <th scope="col">Best for</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">iOS App</th>
                <td>Real-time alerts, quick stats, and chat access to ask the Paulie database about markets, teams, games, and trends.</td>
                <td>Fast checks, alerts on the move, and questions you want answered from your phone.</td>
              </tr>
              <tr>
                <th scope="row">Pro Dashboard</th>
                <td>Full market coverage for price movement across sportsbooks and prediction markets.</td>
                <td>Deeper research, market comparison, and scanning for meaningful trend setups.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="section-card pricing-panel accent-green">
        <div class="section-head">
          <span class="section-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M4 18V6m5 12V9m5 9V4m5 14v-7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              <path d="M3 18.5h18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </span>
          <h2>Pro Dashboard Coverage</h2>
        </div>

        <div class="feature-grid">
          <article class="feature-card">
            <h3>Sportsbook Movement</h3>
            <p>
              Track price data and movement across sportsbooks, including real money movement at
              books like DraftKings.
            </p>
          </article>
          <article class="feature-card">
            <h3>Prediction Markets</h3>
            <p>
              Monitor order books and price movement in prediction markets to see where attention
              and liquidity are building.
            </p>
          </article>
          <article class="feature-card">
            <h3>Public Eye Signals</h3>
            <p>
              Compare sportsbook and prediction-market behavior to identify which teams and games
              are attracting the market's focus.
            </p>
          </article>
          <article class="feature-card">
            <h3>Advanced Trend Tracking</h3>
            <p>
              Use ROI-focused trend views to spot meaningful runs, pressure points, and mean
              reversion opportunities.
            </p>
          </article>
        </div>
      </section>

      <section class="section-card pricing-panel accent-gold">
        <div class="section-head">
          <span class="section-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M4 6.5h16M4 12h16M4 17.5h16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              <path d="M8 4v16M16 4v16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </span>
          <h2>Feature Breakdown</h2>
        </div>

        <div class="pricing-table-wrap">
          <table class="pricing-table">
            <thead>
              <tr>
                <th scope="col">Feature area</th>
                <th scope="col">Included in Paulie Pro</th>
                <th scope="col">Why it matters</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Real-time alerts</th>
                <td>Mobile alerts for notable market opportunities and data abnormalities.</td>
                <td>Move quickly when a market changes instead of finding the setup after the fact.</td>
              </tr>
              <tr>
                <th scope="row">Quick stats</th>
                <td>At-a-glance team, game, and league snapshots.</td>
                <td>Get context fast before deciding whether a deeper dashboard pass is worth it.</td>
              </tr>
              <tr>
                <th scope="row">Database chat</th>
                <td>Ask Paulie anything you want to know across the tracked database.</td>
                <td>Turn research questions into direct prompts instead of manually stitching views together.</td>
              </tr>
              <tr>
                <th scope="row">Market coverage</th>
                <td>Sportsbook pricing, sportsbook money movement, prediction-market prices, and order books.</td>
                <td>See where prices, money, and attention agree or split.</td>
              </tr>
              <tr>
                <th scope="row">ROI trend tracking</th>
                <td>Advanced trend views with ROI context for teams, markets, and recurring angles.</td>
                <td>Spot meaningful trends and potential mean reversions with performance context attached.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="pricing-cta pricing-panel">
        <div>
          <p class="eyebrow">Simple Pricing</p>
          <h2>$24.99 per month. Everything included.</h2>
          <p>No tier maze. Paulie Pro gives you the app, alerts, chat, quick stats, and Pro Dashboard.</p>
        </div>
        <div class="cta-actions">
          <a :href="IOS_APP_STORE_URL" target="_blank" rel="noopener noreferrer" class="btn btn-primary">
            Download iOS App
          </a>
          <router-link to="/pro-dashboard" class="btn btn-secondary">Open Dashboard</router-link>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { IOS_APP_STORE_URL } from '@/lib/appLinks'
</script>

<style scoped>
.pricing-page {
  min-height: 100vh;
  background: transparent;
}

.pricing-container {
  width: min(1100px, 100%);
  margin: 0 auto;
  padding: 1.9rem 0.9rem 3rem;
  display: grid;
  gap: 0.75rem;
}

.pricing-panel {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-sm);
}

.pricing-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 360px);
  align-items: stretch;
  gap: 1rem;
  padding: 1rem;
}

.pricing-hero-copy {
  padding: 0.6rem 0.15rem;
}

.eyebrow {
  color: var(--accent);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 0.45rem;
}

.pricing-hero h1 {
  font-size: clamp(2.2rem, 4.8vw, 3.4rem);
  margin-bottom: 0.7rem;
}

.pricing-hero p,
.pricing-cta p {
  color: var(--text-muted);
  max-width: 760px;
}

.price-card {
  display: grid;
  align-content: center;
  gap: 0.75rem;
  padding: 1rem;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fbfbf8;
}

.price-label {
  color: var(--text-muted);
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.price-row {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.price {
  color: var(--text-primary);
  font-size: clamp(2.3rem, 5vw, 3.4rem);
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1;
}

.term {
  color: var(--text-muted);
  font-size: 1rem;
  font-weight: 700;
}

.price-card p {
  color: var(--text-secondary);
  font-size: 0.92rem;
}

.section-card {
  --section-accent-rgb: 91, 124, 255;
  padding: 1rem 1rem 1.05rem 0.95rem;
  border-left: 3px solid rgb(var(--section-accent-rgb));
}

.accent-blue { --section-accent-rgb: 91, 124, 255; }
.accent-green { --section-accent-rgb: 34, 197, 94; }
.accent-gold { --section-accent-rgb: 251, 191, 36; }

.section-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.section-icon {
  width: 0.95rem;
  height: 0.95rem;
  color: rgb(var(--section-accent-rgb));
}

.section-icon svg {
  display: block;
  width: 100%;
  height: 100%;
}

.section-head h2 {
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.pricing-table-wrap {
  overflow-x: auto;
}

.pricing-table {
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #ffffff;
  box-shadow: var(--shadow-sm);
}

.pricing-table th,
.pricing-table td {
  padding: 0.68rem 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--border);
  vertical-align: top;
}

.pricing-table thead th {
  color: var(--text-muted);
  background: #fbfbf8;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.pricing-table tbody th {
  width: 24%;
  color: var(--text-primary);
  font-size: 0.88rem;
}

.pricing-table tbody td {
  color: var(--text-secondary);
  font-size: 0.88rem;
}

.pricing-table tbody tr:last-child th,
.pricing-table tbody tr:last-child td {
  border-bottom: 0;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
}

.feature-card {
  padding: 0.85rem;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: #fbfbf8;
}

.feature-card h3 {
  font-size: 0.95rem;
  margin-bottom: 0.45rem;
}

.feature-card p {
  color: var(--text-secondary);
  font-size: 0.86rem;
  line-height: 1.55;
}

.pricing-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
}

.pricing-cta h2 {
  font-size: clamp(1.4rem, 2.8vw, 2rem);
  margin-bottom: 0.5rem;
}

.cta-actions {
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

@media (max-width: 920px) {
  .pricing-hero,
  .pricing-cta {
    grid-template-columns: 1fr;
  }

  .feature-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pricing-cta {
    align-items: flex-start;
    flex-direction: column;
  }

  .cta-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 700px) {
  .pricing-container {
    padding: 1rem 0.8rem 2.2rem;
  }

  .pricing-hero,
  .section-card,
  .pricing-cta {
    padding: 0.9rem;
  }

  .feature-grid {
    grid-template-columns: 1fr;
  }

  .pricing-table-wrap {
    overflow: visible;
  }

  .pricing-table,
  .pricing-table tbody,
  .pricing-table tr,
  .pricing-table th,
  .pricing-table td {
    display: block;
    width: 100%;
  }

  .pricing-table thead {
    display: none;
  }

  .pricing-table tr {
    border-bottom: 1px solid var(--border);
  }

  .pricing-table tr:last-child {
    border-bottom: 0;
  }

  .pricing-table tbody th,
  .pricing-table tbody td {
    border-bottom: 0;
  }

  .pricing-table tbody th {
    padding-bottom: 0.25rem;
  }

  .pricing-table tbody td {
    padding-top: 0.25rem;
  }
}
</style>
